import { Controller } from "@hotwired/stimulus"
import WaveSurfer from "wavesurfer.js"
import RecordPlugin from "wavesurfer.js/dist/plugins/record.esm.js"

// data-controller="audio-picker"
export default class extends Controller {
  static targets = [
    "recorderStart", 
    "recorderStop", 
    "recorderPlay", 
    "recorderPause", 
    "fileInput", 
    "soundwave", 
    "soundwavePlaceholder", 
    "timestamp",
    "confirm",
    "discard"
  ]

  connect() {
    this._wavesurfer = null
    this._record = null

    this._onStartClick = this.startRecording.bind(this);
    this._onStopClick = this.stopRecording.bind(this);
    this._onPlayClick = this.playRecording.bind(this);
    this._onPauseClick = this.pauseRecording.bind(this);
    this._onConfirmClick = this.confirmRecording.bind(this);
    this._onDiscardClick = this.discardRecording.bind(this);

    this.recorderStartTarget.addEventListener("click", this._onStartClick);
    this.recorderStopTarget.addEventListener("click", this._onStopClick);
    this.recorderPlayTarget.addEventListener("click", this._onPlayClick);
    this.recorderPauseTarget.addEventListener("click", this._onPauseClick);
    this.confirmTarget.addEventListener("click", this._onConfirmClick);
    this.discardTarget.addEventListener("click", this._onDiscardClick);
    this.showDefaultState()

    this.fileInputTarget.addEventListener("change", () => {
      this.showDefaultState()
    })

    this._initWaveSurfer()
  }

  showDefaultState(){
    this.fileInputTarget.disabled = false
    // Setup wave surfer to use recording buffer?

    this._show(this.recorderStartTarget);
    this._show(this.soundwavePlaceholderTarget);

    this._hide(this.soundwaveTarget);
    this._hide(this.recorderStopTarget);
    this._hide(this.recorderPlayTarget);
    this._hide(this.recorderPauseTarget);
    this._hide(this.confirmTarget);
    this._hide(this.discardTarget);
  }
  showRecordingState(){
    this._show(this.recorderStopTarget);
    this._show(this.soundwaveTarget);

    this._hide(this.soundwavePlaceholderTarget);
    this._hide(this.recorderStartTarget);
    this._hide(this.recorderPlayTarget);
    this._hide(this.recorderPauseTarget);
    this._hide(this.confirmTarget);
    this._hide(this.discardTarget);
  }
  showConfirmationState(){
    this._wavesurfer.setSrc(null, this._blob);

    this._show(this.recorderPlayTarget);
    this._show(this.soundwaveTarget);
    this._show(this.confirmTarget);
    this._show(this.discardTarget);

    this._hide(this.soundwavePlaceholderTarget);
    this._hide(this.recorderStartTarget);
    this._hide(this.recorderStopTarget);
    this._hide(this.recorderPauseTarget);
    this._wavesurfer.toggleInteraction(true)
  }

  showConfirmedState() {
    this.fileInputTarget.disabled = false
    // Setup wave surfer to use recording buffer?

    this._show(this.recorderStartTarget);
    this._show(this.soundwaveTarget);

    this._hide(this.soundwavePlaceholderTarget);
    this._hide(this.recorderStopTarget);
    this._hide(this.recorderPlayTarget);
    this._hide(this.recorderPauseTarget);
    this._hide(this.confirmTarget);
    this._hide(this.discardTarget);
  }

  disconnect() {
    this.recorderStartTarget.removeEventListener("click", this._onStartClick);
    this.recorderStopTarget.removeEventListener("click", this._onStopClick);
    this.recorderPlayTarget.removeEventListener("click", this._onPlayClick);
    this.recorderPauseTarget.removeEventListener("click", this._onPauseClick);
    this.confirmTarget.removeEventListener("click", this._onConfirmClick);
    this.discardTarget.removeEventListener("click", this._onDiscardClick);
    this._destroyWaveSurfer()
  }

  _initWaveSurfer() {
    if (this._wavesurfer) return

    this._record = RecordPlugin.create({
      renderRecordedAudio: true,
      scrollingWaveform: true,
      scrollingWaveformWindow: 5, // seconds
    })

    this._wavesurfer = WaveSurfer.create({
      container: this.soundwaveTarget.querySelector('.wave-container'),
      height: 60,
      barWidth: 3,
      barRadius: 2,
      barGap: 3,
      barMinHeight: 4,
      plugins: [this._record],
      cursorWidth: 0,
      waveColor: "#828282",
      progressColor: "#3CAF9D",
      interact: true,
      normalize: false,
    })

    this._wavesurfer.on('finish', () => {
      this.pauseRecording();
    })

    this._record.on("record-end", (blob) => {
      this._blob = blob;
      this.showConfirmationState();
    })

    this._record.on("device-error", (err) => {
      console.error("[audio-picker] mic device error:", err)
      this._resetUI()
    })

    this._record.on('record-progress', (time) => {
      this.updateProgress(time)
    })
  }

  _destroyWaveSurfer() {
    try {
      if (this._record?.isActive?.()) this._record.stopMic?.()
      if (this._record?.isRecording?.()) this._record.stopRecording?.()
    } catch (_) {}

    this._record?.destroy?.()
    this._record = null

    this._wavesurfer?.destroy?.()
    this._wavesurfer = null
  }

  async startRecording(event) {
    this._wavesurfer.seekTo(0)
    event?.preventDefault?.()

    if (!this._record || !this._wavesurfer) this._initWaveSurfer()

    if (this._record.isRecording() || this._record.isPaused()) return

    try {
      await this._record.startMic()
      await this._record.startRecording()

      this.showRecordingState();
      this.fileInputTarget.disabled = true
    } catch (err) {
      console.error("[audio-picker] startRecording failed:", err)
      this._resetUI()
    }
  }

  stopRecording(event) {
    event?.preventDefault?.()

    if (!this._record) return

    if (this._record.isRecording() || this._record.isPaused()) {
      this._record.stopRecording()
    }

    if (this._record.isActive()) {
      this._record.stopMic()
    }

    this._resetUI()
  }

  playRecording(event) {
    this._wavesurfer.play()
    this._hide(this.recorderPlayTarget)
    this._show(this.recorderPauseTarget)
  }

  pauseRecording(event) {
    this._wavesurfer.pause()
    this._show(this.recorderPlayTarget)
    this._hide(this.recorderPauseTarget)
  }

  confirmRecording(event) {
    this._wavesurfer.pause();
    this.showConfirmedState();
    this._wavesurfer.toggleInteraction(false)
    this._wavesurfer.seekTo(1)
    this._persistBlobToFileInput(this._blob)
  }

  discardRecording(event){
    this._wavesurfer.pause();
    this.showDefaultState();
    this._wavesurfer.toggleInteraction(false)
    this._wavesurfer.seekTo(0)
    this.updateProgress(0)
  }

  _persistBlobToFileInput(blob) {
    if (!blob || blob.size === 0) return

    const blobType = blob.type || "audio/webm"
    const ext = this._extensionForMime(blobType) || "webm"
    const filename = `recording-${new Date().toISOString().replace(/[:.]/g, "-")}.${ext}`

    const file = new File([blob], filename, { type: blobType })

    const dt = new DataTransfer()
    dt.items.add(file)
    this.fileInputTarget.files = dt.files
  }

  _extensionForMime(mime) {
    if (!mime) return null
    if (mime.includes("webm")) return "webm"
    if (mime.includes("ogg")) return "ogg"
    if (mime.includes("mp4")) return "m4a"
    if (mime.includes("wav")) return "wav"
    return null
  }

  _resetUI() {
    this.showDefaultState()
  }

  _hide(el) {
    el.classList.add("tw:hidden")
  }

  _show(el) {
    el.classList.remove("tw:hidden")
  }

  updateProgress(time) {
    const formattedTime = [
      Math.floor((time % 3600000) / 60000),
      Math.floor((time % 60000) / 1000),
    ]
      .map((v) => (v < 10 ? '0' + v : v))
      .join(':')
    this.timestampTarget.textContent = formattedTime
  }
}
